import { motion } from "framer-motion";
import { useEffect, useState } from "react";

import frame1 from "../../../assets/backgrounds/bimer/frame_1.svg";
import frame2 from "../../../assets/backgrounds/bimer/frame_2.svg";
import frame3 from "../../../assets/backgrounds/bimer/frame_3.svg";
import clap from "../../../assets/backgrounds/bimer/clap.svg";

import batimentDroite1 from "../../../assets/backgrounds/bimer/batiment_droite_1.svg";
import batimentGauche1 from "../../../assets/backgrounds/bimer/batiment_gauche_1.svg";
import batimentDroite2 from "../../../assets/backgrounds/bimer/batiment_droite_2.svg";
import batimentGauche2 from "../../../assets/backgrounds/bimer/batiment_gauche_2.svg";

import batimentConstructibleBase from "../../../assets/backgrounds/bimer/base.svg";
import batimentConstructibleToit from "../../../assets/backgrounds/bimer/toit.svg";

const calculateDimension = (screenSize, percentage, min, max) => {
	const value = screenSize * percentage;
	return Math.max(min, Math.min(value, max));
};

const calculateWidth = (screenWidth, multiplier, minWidth, maxWidth) => {
	const baseWidth = screenWidth * multiplier;
	return Math.min(baseWidth, maxWidth);
};

const BimerAnimatedBackground = ({
	clapTriggered,
	newFloorTriggered,
	roofFloorTriggered,
}) => {
	const [width, setWidth] = useState(window.innerWidth);
	const [height, setHeight] = useState(window.innerHeight);

	const handleResize = () => {
		setWidth(window.innerWidth);
		setHeight(window.innerHeight);
	};

	const [currentFrameIndex, setCurrentFrameIndex] = useState(0);
	const [floors, setFloors] = useState([]);
	const [roofFloor, setRoofFloor] = useState(null);

	const frames = [frame1, frame2, frame3];

	useEffect(() => {
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	useEffect(() => {
		const intervalId = setInterval(() => {
			setCurrentFrameIndex((prev) => (prev + 1) % frames.length);
		}, 200);
		return () => clearInterval(intervalId);
	}, [frames.length]);

	useEffect(() => {
		if (newFloorTriggered) {
			setFloors((prevFloors) => [
				...prevFloors,
				{ id: prevFloors.length, stackIndex: prevFloors.length },
			]);
		}
	}, [newFloorTriggered]);

	useEffect(() => {
		if (roofFloorTriggered) {
			setRoofFloor({
				id: "roofFloor",
				stackIndex: floors.length,
			});
		}
	}, [roofFloorTriggered, floors.length]);

	// Calculate dimensions based on screen size
	const floorHeight = calculateDimension(height, 0.08, 50, 100); // Floor height: 8% of screen height
	const roofHeight = floorHeight; // Match floor height for visual consistency

	const leftBuildingWidth1 = calculateWidth(width, 0.2, 80, 320);
	const leftBuildingWidth2 = calculateWidth(width, 0.3, 250, 580);
	const rightBuildingWidth1 = calculateWidth(width, 0.3, 200, 580);
	const rightBuildingWidth2 = calculateWidth(width, 0.2, 200, 400);

	const characterSize = calculateDimension(width, 0.08, 50, 100); // Character width: 8% of screen width

	return (
		<>
			{/* Background */}
			<div className="absolute inset-0 w-full h-full bg-gradient-to-t from-[#6394bf] to-[#89c0f0] z-0" />

			<div className="absolute bottom-0 left-0 w-full h-[25vh] bg-gradient-to-t from-gray-400 to-gray-300 z-0" />

			{/* Buildings */}
			<motion.img
				src={batimentGauche2}
				alt="Left Building"
				style={{
					width: `${leftBuildingWidth2}px`,
					left: `${leftBuildingWidth1 / 2}px`,
				}}
				className="absolute bottom-[20vh] z-1"
			/>
			<motion.img
				src={batimentGauche1}
				alt="Left Building"
				style={{ width: `${leftBuildingWidth1}px` }}
				className="absolute left-0 bottom-[20vh] z-1"
			/>
			<motion.img
				src={batimentDroite1}
				alt="Right Building"
				style={{
					width: `${rightBuildingWidth1}px`,
					right: `${-rightBuildingWidth1 / 2}px`,
				}}
				className="absolute bottom-[20vh] z-10"
			/>
			<motion.img
				src={batimentDroite2}
				alt="Right Building"
				style={{
					width: `${rightBuildingWidth2}px`,
					right: `${rightBuildingWidth1 / 2}px`,
				}}
				className="absolute bottom-[20vh] z-1"
			/>

			{/* Character */}
			<motion.img
				src={clapTriggered ? clap : frames[currentFrameIndex]}
				alt="Character Animation"
				style={{
					width: `${characterSize}px`,
					left: "40%",
					transform: "translateX(-40%)",
				}}
				className="absolute bottom-[12vh] z-10"
			/>

			{/* Floors */}
			{floors.map((floor) => (
				<motion.img
					key={floor.id}
					src={batimentConstructibleBase}
					alt="Floor"
					initial={{ y: "-1000%" }}
					animate={{ y: `calc(400% - ${floor.stackIndex * floorHeight}px)` }}
					transition={{ duration: 0.8, ease: "easeOut" }}
					style={{
						height: `${floorHeight}px`,
						left: "60%",
						transform: "translateX(-60%)",
					}}
					className="absolute z-10"
				/>
			))}

			{/* Roof */}
			{roofFloor && (
				<motion.img
					key={roofFloor.id}
					src={batimentConstructibleToit}
					alt="Roof"
					initial={{ y: "-1000%" }}
					animate={{
						y: `calc(400% - ${roofFloor.stackIndex * floorHeight * 1.16}px)`,
					}}
					transition={{ duration: 1, ease: "easeOut" }}
					style={{
						height: `${roofHeight * 1.25}px`,

						left: "60%",
						transform: "translateX(-60%)",
					}}
					className="absolute z-10"
				/>
			)}
		</>
	);
};

export default BimerAnimatedBackground;
